import React from 'react';

const TermsContent = () => {
  return (
    <div className="prose prose-indigo max-w-none">
      <h2>1. Introduction</h2>
      <p>Welcome to SubleaseHero ("we," "us," or "our"), a service operated by 21st Q LTD, a Nevada Limited Liability Company. These Terms and Conditions ("Terms") govern your access to and use of our website and services ("Services"). By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not access or use our Services.</p>

      <h2>2. Definitions</h2>
      <ul>
        <li><strong>User(s):</strong> Any individual or entity accessing or using our Services, including Hosts and Guests.</li>
        <li><strong>Host:</strong> A User who lists a property for sublease.</li>
        <li><strong>Guest:</strong> A User who expresses interest in or enters into a sublease agreement for a listed property.</li>
        <li><strong>Listing:</strong> A property advertised on our platform for sublease.</li>
        <li><strong>Content:</strong> All text, images, video, audio, or other material provided by Users on the Services.</li>
      </ul>

      <h2>3. Eligibility</h2>
      <p>By using our Services, you represent and warrant that:</p>
      <ul>
        <li>You are at least 18 years old and have the legal capacity to enter into a binding agreement.</li>
        <li>You have not been previously suspended or removed from using our Services.</li>
        <li>Your use of the Services does not violate any applicable laws, regulations, or contractual obligations.</li>
      </ul>

      <h2>4. Account Registration</h2>
      <ul>
        <li><strong>Account Creation:</strong> To access certain features of the Services, you must register for an account by providing accurate, current, and complete information.</li>
        <li><strong>Account Security:</strong> You are solely responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use.</li>
        <li><strong>Account Termination:</strong> We reserve the right to suspend or terminate your account at our sole discretion, without notice or liability, for any reason, including but not limited to breach of these Terms.</li>
      </ul>

      <h2>5. User Conduct</h2>
      <p>You agree not to engage in any of the following prohibited activities:</p>
      <ul>
        <li><strong>Illegal Activities:</strong> Violating any local, state, national, or international laws or regulations.</li>
        <li><strong>Misrepresentation:</strong> Providing false, misleading, or deceptive information.</li>
        <li><strong>Infringement:</strong> Posting content that infringes on the intellectual property rights of others.</li>
        <li><strong>Harassment:</strong> Engaging in threatening, abusive, or harassing behavior.</li>
        <li><strong>Unauthorized Access:</strong> Attempting to interfere with or compromise the integrity or security of the Services.</li>
        <li><strong>Commercial Use:</strong> Using the Services for any commercial purposes not expressly permitted by these Terms.</li>
      </ul>

      <h2>6. Host Responsibilities</h2>
      <p>Hosts are solely responsible for:</p>
      <ul>
        <li><strong>Legal Compliance:</strong> Ensuring they have the legal right and necessary permissions to sublease their property, including obtaining consent from landlords or property managers.</li>
        <li><strong>Accurate Listings:</strong> Providing complete, accurate, and up-to-date information in their Listings.</li>
        <li><strong>Risk Assessment:</strong> Conducting appropriate background checks and due diligence on potential Guests.</li>
        <li><strong>Taxes and Fees:</strong> Reporting and remitting any applicable taxes, fees, or other charges arising from subleasing activities.</li>
      </ul>

      <h2>7. Guest Responsibilities</h2>
      <p>Guests are solely responsible for:</p>
      <ul>
        <li><strong>Verification:</strong> Independently verifying the accuracy of Listings and the suitability of properties.</li>
        <li><strong>Legal Compliance:</strong> Complying with all applicable laws, regulations, and lease terms when entering into a sublease agreement.</li>
        <li><strong>Financial Obligations:</strong> Fulfilling all payment obligations under the sublease agreement and these Terms.</li>
        <li><strong>Risk Assumption:</strong> Assuming all risks associated with entering into subleasing arrangements.</li>
      </ul>

      <h2>8. Listings and Content</h2>
      <ul>
        <li><strong>Content Ownership:</strong> Users retain ownership of the Content they submit but grant SubleaseHero and 21st Q LTD a worldwide, perpetual, non-exclusive, royalty-free, transferable license to use, reproduce, distribute, and display such Content in connection with the Services.</li>
        <li><strong>Content Standards:</strong> All Content must comply with these Terms and all applicable laws and regulations. We reserve the right to remove any Content that violates these Terms or is otherwise objectionable.</li>
        <li><strong>No Endorsement:</strong> SubleaseHero does not endorse any Users, Listings, or properties. We are not responsible for any Content provided by Users.</li>
      </ul>

      <h2>9. Fees and Payments</h2>
      <ul>
        <li><strong>Service Fees:</strong> We charge a non-refundable 5% service fee based on one month's rent when a sublease match is made. This fee is collected as a good faith deposit from the Guest.</li>
        <li><strong>Additional Fees:</strong> Additional fees may apply for premium services or features, which will be disclosed prior to purchase.</li>
        <li><strong>Non-Refundable:</strong> All fees paid are non-refundable, except as expressly stated in these Terms.</li>
        <li><strong>Payment Processing:</strong> Payments are facilitated through third-party payment processors. SubleaseHero and 21st Q LTD disclaim all liability arising from errors or issues related to payment processing.</li>
      </ul>

      {/* Continue with sections 10-28... */}
      
      <h2>10. Disclaimers and Limitation of Liability</h2>
      <p className="font-bold">PLEASE READ THIS SECTION CAREFULLY, AS IT LIMITS OUR LIABILITY TO THE MAXIMUM EXTENT PERMITTED UNDER APPLICABLE LAW.</p>
      <ul>
        <li><strong>No Warranty:</strong> The Services are provided on an "AS IS" and "AS AVAILABLE" basis, without warranties of any kind, either express or implied.</li>
        <li><strong>No Liability for User Conduct:</strong> SubleaseHero and 21st Q LTD are not responsible or liable for the actions, Content, or information of Users or third parties.</li>
        <li><strong>Maximum Liability:</strong> In no event shall SubleaseHero's and 21st Q LTD's total liability exceed the amount of fees you have paid to SubleaseHero in the six (6) months preceding the event giving rise to the liability or one hundred U.S. dollars ($100), whichever is greater.</li>
      </ul>

      {/* Add remaining sections... */}

      <h2>28. Acknowledgment</h2>
      <p className="font-bold">BY USING OR ACCESSING THE SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS AND AGREE TO BE BOUND BY THEM.</p>

      <hr className="my-8" />

      <p className="text-sm text-gray-500">
        <em>Disclaimer: This document is provided for informational purposes only and does not constitute legal advice. Laws and regulations may vary by jurisdiction, and you should consult a qualified attorney to ensure that your Terms and Conditions are compliant with all applicable laws and regulations.</em>
      </p>
    </div>
  );
};

export default TermsContent;